import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  ReactNode,
} from 'react';
import NotificationSnackbar from '../components/NotificationSnackbar';

export interface AppNotification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title?: string;
  message: string;
  duration?: number;
}

interface NotificationContextValue {
  notify: (notification: Omit<AppNotification, 'id'> & { id?: string }) => void;
  notifySuccess: (message: string, title?: string) => void;
  notifyError: (message: string, title?: string) => void;
}

const NotificationContext = createContext<NotificationContextValue | undefined>(undefined);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [activeNotification, setActiveNotification] = useState<AppNotification | null>(null);

  const notify = useCallback((notification: Omit<AppNotification, 'id'> & { id?: string }) => {
    setActiveNotification({
      ...notification,
      id: notification.id || `notification-${Date.now()}`,
    });
  }, []);

  const notifySuccess = useCallback((message: string, title?: string) => {
    notify({ type: 'success', message, title });
  }, [notify]);

  const notifyError = useCallback((message: string, title?: string) => {
    notify({ type: 'error', message, title });
  }, [notify]);

  const clearNotification = useCallback(() => {
    setActiveNotification(null);
  }, []);

  const value = useMemo(
    () => ({ notify, notifySuccess, notifyError }),
    [notify, notifySuccess, notifyError],
  );

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationSnackbar notification={activeNotification} onClose={clearNotification} />
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextValue => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};
